import React, { useEffect } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAPI } from '../../../../Contexts/APIContext'
import { useInView } from 'react-intersection-observer'
import { formatStringInNav } from '../../../../Helpers/String+Extension'
import { Breadcrumbs } from '../../../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import { useArtistSongsStore } from '../../../../Storage/artistSongsPage/useArtistSongsStore'
import DebounceSearch from '../../../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import SongGridItem from '../../../../Components/DashboardComponents/SongGridItem/SongGridItem'
import OptionsList from '../../../../Components/GeneralComponents/OptionsList/OptionsList'
import Loader from '../../../../Components/GeneralComponents/Loader'
import classes from './index.module.css'
import useGetArtistData from '../../../../Helpers/hooks/useGetArtistData'
import useClearArtistSearchTerms from '../../../../Helpers/hooks/useClearArtistSearchTerms'
import ArtistTopTeaseGridItem from '../../../../Components/ArtistProfileComponents/ArtistTopTeaseGridItem'

export default function Step1() {
  const pageLimit = 20
  const navigate = useNavigate()
  const { getArtistSongs, getArtistTeases, getSearchAllSongs } = useAPI()
  const { artist_name } = useParams()
  const [ref, inView] = useInView()
  const { pathname } = useLocation()
  const isTease = pathname.includes('tease')
  const getAll = isTease ? getArtistTeases : getArtistSongs

  // zustand storage
  const options = useArtistSongsStore(state => state.options)

  const selectedOption = useArtistSongsStore(state => state.selectedOption)
  const setSelectedOption = useArtistSongsStore(
    state => state.setSelectedOption
  )

  const { searchTerm, setSearchTerm, inputValue, setInputValue } =
    useClearArtistSearchTerms({ artistName: artist_name })

  const {
    data: dataArtist,
    isLoading: isLoadingArtist,
    isFetching: isFetchingArtist,
    isError: isErrorArtist,
    error: errorArtist,
  } = useGetArtistData({ artistName: artist_name })

  const {
    data,
    isError,
    error,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['artistSongsList', artist_name, searchTerm, selectedOption.id],
    queryFn: ({ pageParam = 1 }) => {
      return searchTerm === ''
        ? getAll(artist_name, pageParam, pageLimit, selectedOption.id)
        : getSearchAllSongs(searchTerm, pageParam, pageLimit, dataArtist.f_id)
    },
    getNextPageParam: pageData => {
      const nextPage = pageData.p + 1
      const newPage =
        pageData.p * pageLimit < pageData.songs_count ? nextPage : undefined
      return newPage
    },
    enabled: !!dataArtist && !isLoadingArtist && !isFetchingArtist,
  })

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [inView])

  return (
    <div className={classes.wrapper}>
      {!isLoading && !!dataArtist && !isLoadingArtist && !isFetchingArtist && (
        <>
          <Breadcrumbs
            items={[
              {
                text: 'Artists',
                onClick: () => {
                  navigate('/artists')
                },
              },
              {
                text: formatStringInNav(dataArtist.name),
                onClick: () => {
                  navigate('/artist/' + artist_name)
                },
              },
              {
                text: !isTease ? 'Songs' : 'Teases',
                onClick: () => {},
              },
            ]}
          />
          <div className={classes.header}>
            <div>
              <h1 className={classes.title}>{`${
                !isTease ? 'Songs' : 'Teases'
              } by ${dataArtist.name}`}</h1>
              <p className={classes.subtitle}>
                {data?.pages[0]
                  ? `Total ${!isTease ? 'Songs' : 'Teases'} ` +
                    data?.pages[0]?.songs_count
                  : ''}
              </p>
            </div>
            <div className={classes.searchSortContainer}>
              {!searchTerm ? (
                <OptionsList
                  selectedOption={selectedOption}
                  setSelectedOption={option => setSelectedOption(option)}
                  options={options}
                  label={'Sort by '}
                  disabled={!!searchTerm}
                />
              ) : null}
              <DebounceSearch
                inputValue={inputValue}
                setInputValue={setInputValue}
                searchValue={searchTerm}
                setSearchValue={setSearchTerm}
                placeholder={`Search for ${!isTease ? 'Songs' : 'Teases'}...`}
              />
            </div>
          </div>
        </>
      )}
      <div className={classes.songs}>
        {!isError &&
          data &&
          data.pages.map((page, index) => (
            <React.Fragment key={index}>
              {page.songs.length > 0 ? (
                page.songs.map(song => (
                  <div key={song.f_id} ref={ref}>
                    {!isTease ? (
                      <SongGridItem
                        key={song.f_id}
                        song={song}
                        onClick={song => {
                          navigate('/create-new-feel-step-2/' + song.f_id)
                        }}
                      />
                    ) : (
                      <ArtistTopTeaseGridItem
                        key={song.f_id}
                        song={song}
                        onClick={song => {
                          navigate('/create-new-feel-step-2/' + song.f_id)
                        }}
                      />
                    )}
                  </div>
                ))
              ) : (
                <h1 className={classes.emptyResultText}>No Results</h1>
              )}
            </React.Fragment>
          ))}
      </div>
      {(isError || isErrorArtist) && (
        <h1 className={classes.errorText}>
          Error {error.status || errorArtist.status}
        </h1>
      )}
      <Loader
        isLoading={
          isLoadingArtist || isFetchingArtist || isLoading || isFetchingNextPage
        }
      />
    </div>
  )
}
